/**
 * Composer-Anhang-Flow (UI-Overhaul „Anhang-Flow", 2026-07-07).
 *
 * Claude.ai-Muster: 📎 / Drag&Drop startet SOFORT den Hintergrund-Upload
 * (bestehender POST /documents, FormData `file` [+ optional `project_id`]).
 * Während Upload/Verarbeitung zeigt der Composer einen Anhang-Chip; das
 * Senden nutzt exakt den „Mit Dokument unterhalten"-Mechanismus
 * (DocumentDetail.tsx:136) = Chat im PROJEKT des Dokuments anlegen, Retrieval
 * ist über chat.project_id gescopet (chats.py:480). Es gibt KEINEN neuen
 * Backend-Pfad und keine Per-Dokument-Referenz — die Projekt-Zuordnung IST
 * der Bezug.
 *
 * Der × am Chip entfernt nur die (transiente) Chat-Verknüpfung im Composer,
 * das hochgeladene Dokument bleibt bestehen.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Check, X, AlertCircle } from "lucide-react";
import { api, postForm } from "../api";
import { useToast } from "../ui/toast";

/** CustomEvent, mit dem der Home-Chip „Dokument hochladen" den Datei-Dialog
 *  des Home-Composers öffnet (statt zu /docs zu navigieren). */
export const ATTACH_DOC_EVENT = "atlas:attach-doc";

/** Vom 📎/DnD akzeptierte Endungen (Untermenge der Backend-Whitelist). */
export const ATTACH_ACCEPT = ".pdf,.md,.markdown,.txt";

interface DocOut {
  id: string;
  title: string;
  project_id: string | null;
  embedding_status: string;
}

export type AttachStatus = "uploading" | "processing" | "ready" | "error";

export interface Attachment {
  fileName: string;
  status: AttachStatus;
  /** Erst gesetzt, wenn der Upload eine Dokument-Id zurückgegeben hat. */
  doc: DocOut | null;
  error?: string;
}

/** embedding_status-Werte, die noch als „in Arbeit" gelten (DocumentDetail.tsx). */
const ACTIVE = ["pending", "doc_ai_pending", "processing"];

function statusOf(d: DocOut): AttachStatus {
  if (d.embedding_status === "error") return "error";
  if (d.embedding_status === "done") return "ready";
  return "processing";
}

export interface DocAttachmentApi {
  attachment: Attachment | null;
  inputRef: React.RefObject<HTMLInputElement>;
  openDialog: () => void;
  onFile: (file: File) => void;
  clear: () => void;
  /** Dokument-Id vorhanden und nicht fehlerhaft → Senden mit Doc-Bezug möglich. */
  hasDoc: boolean;
  /** Upload läuft noch (keine Id) → Senden blockieren, bis die Id da ist. */
  uploading: boolean;
}

/**
 * `projectId` = Ziel-Projekt des Composers. Der Upload landet in diesem Projekt
 * (bei "" / null: Backend-Fallback auf „Allgemein"). Wichtig, damit das Doc im
 * selben Projekt liegt wie der Chat und vom Retrieval gefunden wird.
 */
export function useDocAttachment(
  projectId: string | null | undefined
): DocAttachmentApi {
  const toast = useToast();
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeDocRef = useRef<string | null>(null);

  const stopPoll = () => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  };

  const clear = useCallback(() => {
    stopPoll();
    activeDocRef.current = null;
    setAttachment(null);
  }, []);

  // Poll-Cleanup beim Unmount.
  useEffect(() => () => stopPoll(), []);

  // Verarbeitungs-Status pollen (analog DocumentDetail.tsx, fester 5s-Takt).
  const poll = useCallback((docId: string) => {
    const tick = async () => {
      if (activeDocRef.current !== docId) return;
      try {
        const d = await api.get<DocOut>(`/documents/${docId}`);
        if (activeDocRef.current !== docId) return;
        setAttachment((a) =>
          a && a.doc && a.doc.id === docId
            ? { ...a, doc: d, status: statusOf(d) }
            : a
        );
        if (ACTIVE.includes(d.embedding_status)) {
          pollRef.current = setTimeout(tick, 5000);
        }
      } catch {
        /* still — Poll endet, Chip behält letzten Status */
      }
    };
    pollRef.current = setTimeout(tick, 4000);
  }, []);

  const onFile = useCallback(
    async (file: File) => {
      stopPoll();
      activeDocRef.current = null;
      setAttachment({ fileName: file.name, status: "uploading", doc: null });
      try {
        const fd = new FormData();
        fd.append("file", file);
        if (projectId) fd.append("project_id", projectId);
        const doc = await postForm<DocOut>("/documents", fd);
        activeDocRef.current = doc.id;
        setAttachment({ fileName: file.name, status: statusOf(doc), doc });
        if (ACTIVE.includes(doc.embedding_status)) poll(doc.id);
      } catch (e) {
        activeDocRef.current = null;
        const msg = (e as Error).message;
        setAttachment({
          fileName: file.name,
          status: "error",
          doc: null,
          error: msg,
        });
        toast.err("Upload fehlgeschlagen", msg);
      }
    },
    [projectId, poll, toast]
  );

  const openDialog = useCallback(() => inputRef.current?.click(), []);

  const hasDoc = !!attachment?.doc && attachment.status !== "error";
  const uploading = attachment?.status === "uploading";

  return { attachment, inputRef, openDialog, onFile, clear, hasDoc, uploading };
}

/** Verstecktes File-Input — an `api.inputRef` gebunden, ruft `onFile`. */
export function AttachInput({ api: a }: { api: DocAttachmentApi }) {
  return (
    <input
      ref={a.inputRef}
      type="file"
      accept={ATTACH_ACCEPT}
      className="sr-only"
      aria-hidden="true"
      tabIndex={-1}
      onChange={(e) => {
        const f = e.target.files?.[0];
        if (f) a.onFile(f);
        e.target.value = ""; // gleiche Datei erneut wählbar
      }}
    />
  );
}

/** Anhang-Chip: Dateiname + Status-Indikator + entfernen (×). */
export function AttachmentChip({
  attachment,
  onRemove,
}: {
  attachment: Attachment;
  onRemove: () => void;
}) {
  const { status, fileName } = attachment;
  const tone =
    status === "error"
      ? "border-danger/40 bg-danger-soft text-danger"
      : status === "ready"
        ? "border-success/40 bg-success-soft text-success"
        : "border-border bg-surface-2 text-text-2";
  const label =
    status === "uploading"
      ? "lädt …"
      : status === "processing"
        ? "verarbeite …"
        : status === "error"
          ? "Fehler"
          : "";
  return (
    <div
      className={
        "inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded-full border text-xs " +
        tone
      }
    >
      {status === "uploading" || status === "processing" ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
      ) : status === "ready" ? (
        <Check className="w-3.5 h-3.5 shrink-0" />
      ) : (
        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
      )}
      <span className="truncate max-w-[12rem]">{fileName}</span>
      {label && <span className="shrink-0 opacity-70">{label}</span>}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Anhang entfernen"
        title="Anhang entfernen (Dokument bleibt erhalten)"
        className="shrink-0 ml-0.5 rounded-full p-0.5 hover:bg-black/5 dark:hover:bg-white/10"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
